import React, { useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaEnvelope, FaCopy, FaFileDownload, FaPlus, FaCheck, FaBolt, FaRandom, FaGlobe, FaVenusMars, FaServer, FaCloud } from 'react-icons/fa';

import {
  TechSection,
  TechButton,
  TechButtonGhost,
  TechInput,
  TechSelect,
  TechTextarea,
  NumberedList,
  NumberedItem,
  mono,
  borderSoft,
  panelDeep,
  accent,
} from '../../styles/techComponents';

import { generateRandomProfiles, generateSecurePassword, PAISES } from './randomProfileFactory';
import { batchCreateProfiles, createEmailAccounts } from '../../services/digitalProfilesService';
import { Label, Hint, FormRow } from './CreadorPerfilesStyles';

const MAX_CHUNK = 20;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.6rem;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;
`;

const EmailCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.7rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${(p) => p.$real ? 'var(--alert-success, #38d39f)' : accent};
  border-radius: 6px;
  font-size: 0.82rem;

  .email {
    font-family: ${mono};
    font-size: 0.78rem;
    color: var(--text-primary);
    word-break: break-all;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.65rem;
    color: var(--text-disabled);
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

const StatRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  font-family: ${mono};
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const Message = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  margin-top: 0.75rem;
  background: ${(p) => p.$ok ? 'rgba(56, 211, 159, 0.08)' : 'rgba(239, 71, 111, 0.08)'};
  border: 1px solid ${(p) => p.$ok ? 'rgba(56, 211, 159, 0.3)' : 'rgba(239, 71, 111, 0.3)'};
  border-left: 3px solid ${(p) => p.$ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error, #ff5470)'};
  color: ${(p) => p.$ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error, #ff5470)'};
`;

const GENDERS = [
  { value: '', label: 'Aleatorio' },
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Femenino' },
  { value: 'U', label: 'Unisex' },
];

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'x', label: 'X / Twitter' },
];

const DOMAINS = [
  { value: '', label: 'Aleatorio' },
  { value: 'gmail.com', label: 'gmail.com' },
  { value: 'outlook.com', label: 'outlook.com' },
  { value: 'yahoo.com', label: 'yahoo.com' },
  { value: 'hotmail.com', label: 'hotmail.com' },
  { value: 'icloud.com', label: 'icloud.com' },
  { value: 'custom', label: 'Personalizado…' },
];

const PROVIDERS = [
  { value: 'catchall', label: 'Catch-all propio (cuentas.adbize.com)' },
  { value: 'mailtm-browser', label: 'mail.tm (browser real)' },
  { value: 'mailtm-api', label: 'mail.tm (API directa)' },
];

function buildAccountRows(profiles) {
  return profiles.map((p, idx) => ({
    n: idx + 1,
    platform: p.platform,
    username: p.username,
    display_name: p.display_name,
    email: p.email_address,
    password: p.password,
    login_user: p.login_user,
    zona_horaria: p.zona_horaria,
    pais: p._meta?.pais || '',
    ciudad: p._meta?.ciudad || '',
    gender: p._meta?.gender || '',
  }));
}

function toCsv(rows) {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))];
  return lines.join('\n');
}

function generateInChunks({ platform, count, base, seedBase }) {
  let remaining = Math.max(1, Math.min(Number(count) || 1, 100));
  const out = [];
  let chunkIndex = 0;
  while (remaining > 0 && chunkIndex < 10) {
    const n = Math.min(remaining, MAX_CHUNK);
    const chunk = generateRandomProfiles({ platform, count: n, base, seed: `${seedBase}-${chunkIndex}` });
    out.push(...chunk);
    remaining -= n;
    chunkIndex += 1;
  }
  return out;
}

const GeneradorEmails = ({ onCreated }) => {
  const [platform, setPlatform] = useState('instagram');
  const [count, setCount] = useState(10);
  const [gender, setGender] = useState('');
  const [pais, setPais] = useState('');
  const [domainMode, setDomainMode] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [includePassword, setIncludePassword] = useState(true);
  const [withProfiles, setWithProfiles] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [generated, setGenerated] = useState([]);
  const [copied, setCopied] = useState(null);
  const [useRealProvider, setUseRealProvider] = useState(true);
  const [providerMode, setProviderMode] = useState('catchall');
  const [emailErrors, setEmailErrors] = useState([]);

  const base = useMemo(() => {
    const base = {};
    if (gender) base.gender = gender;
    if (pais) base.pais = pais;
    if (domainMode === 'custom' && customDomain.trim()) {
      base.emailDomain = customDomain.trim();
    } else if (domainMode) {
      base.emailDomain = domainMode;
    }
    return base;
  }, [gender, pais, domainMode, customDomain]);

  const handleGenerate = async () => {
    setBusy(true);
    setMessage(null);
    setEmailErrors([]);
    try {
      const seedBase = Date.now();
      const profiles = generateInChunks({ platform, count, base, seedBase });

      if (useRealProvider) {
        const isCatchall = providerMode === 'catchall';
        const useBrowser = providerMode === 'mailtm-browser';
        const provider = isCatchall ? 'catchall' : 'mailtm';
        setMessage({ ok: true, text: isCatchall ? `Generando ${count} direcciones en cuentas.adbize.com (catch-all propio)…` : `Solicitando ${count} emails reales al servidor (modo ${useBrowser ? 'browser' : 'API'})…` });
        const { accounts, errors } = await createEmailAccounts({ count, useBrowser, provider });
        if (errors?.length) setEmailErrors(errors);

        const realEmails = accounts || [];
        const combined = profiles.map((p, idx) => {
          const real = realEmails[idx];
          if (!real) return p;
          return {
            ...p,
            email_address: real.address,
            login_user: real.address,
            password: real.password,
            _emailMeta: {
              id: real.id,
              provider: real.provider,
              domain: real.domain,
              browser_emulated: real.browser_emulated,
            },
          };
        });
        setGenerated(combined);
        const okCount = realEmails.length;
        const failCount = errors?.length || Math.max(0, count - okCount);
        setMessage({
          ok: failCount === 0,
          text: `Emails reales creados: ${okCount}. Fallidos: ${failCount}.`,
        });
      } else {
        setGenerated(profiles);
        setMessage({ ok: true, text: `Se generaron ${profiles.length} emails locales listos para usar.` });
      }
    } catch (err) {
      setMessage({ ok: false, text: err?.response?.data?.error || err?.message || 'Error al generar emails.' });
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = () => {
    setGenerated([]);
    handleGenerate();
  };

  const handleCreateProfiles = async () => {
    if (!generated.length) return;
    setBusy(true);
    setMessage(null);
    try {
      const accounts = generated.map((p) => ({
        platform: p.platform,
        username: p.username,
        login_user: p.login_user,
        email_address: p.email_address,
        password: p.password,
        display_name: p.display_name,
        zona_horaria: p.zona_horaria,
        prompt_personalidad: p.prompt_personalidad,
        intereses: p.intereses,
        frecuencia_posteo: p.frecuencia_posteo,
        tasa_interaccion: p.tasa_interaccion,
        max_likes_diarios: p.max_likes_diarios,
        max_posts_diarios: p.max_posts_diarios,
        hora_inicio_sueno: p.hora_inicio_sueno,
        hora_fin_sueno: p.hora_fin_sueno,
      }));
      const result = await batchCreateProfiles(accounts);
      setMessage({
        ok: true,
        text: `Creados ${result?.created || accounts.length} perfiles digitales. Refrescá la lista para verlos.`,
      });
      if (onCreated) onCreated();
      setGenerated([]);
    } catch (err) {
      setMessage({ ok: false, text: friendlyError(err, 'Error al crear perfiles.') });
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setMessage({ ok: false, text: 'No se pudo copiar al portapapeles.' });
    }
  };

  const copyEmails = () => {
    const text = generated.map((p) => p.email_address).join('\n');
    copyToClipboard(text, 'emails');
  };

  const copyCsv = () => {
    const rows = buildAccountRows(generated);
    copyToClipboard(toCsv(rows), 'csv');
  };

  const downloadCsv = () => {
    const rows = buildAccountRows(generated);
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emails-${platform}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <TechSection title="Generador masivo de emails" style={{ marginBottom: '1.25rem', borderColor: accent }}>
      <Grid>
        <FormRow>
          <Label><FaGlobe /> Plataforma objetivo</Label>
          <TechSelect value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </TechSelect>
          <Hint>Los perfiles se crearán para esta plataforma.</Hint>
        </FormRow>

        <FormRow>
          <Label><FaEnvelope /> Cantidad</Label>
          <TechInput
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
          />
          <Hint>Máximo 100 emails por lote.</Hint>
        </FormRow>

        <FormRow>
          <Label><FaVenusMars /> Género</Label>
          <TechSelect value={gender} onChange={(e) => setGender(e.target.value)}>
            {GENDERS.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </TechSelect>
          <Hint>Influencia en nombres y apellidos.</Hint>
        </FormRow>

        <FormRow>
          <Label><FaGlobe /> País</Label>
          <TechSelect value={pais} onChange={(e) => setPais(e.target.value)}>
            <option value="">Aleatorio</option>
            {PAISES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </TechSelect>
          <Hint>Define ciudad y zona horaria.</Hint>
        </FormRow>

        <FormRow>
          <Label><FaEnvelope /> Dominio</Label>
          <TechSelect value={domainMode} onChange={(e) => setDomainMode(e.target.value)}>
            {DOMAINS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </TechSelect>
          {domainMode === 'custom' && (
            <TechInput
              placeholder="ej. adbize.com.ar"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              style={{ marginTop: 6 }}
            />
          )}
        </FormRow>
      </Grid>

      <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={useRealProvider}
            onChange={(e) => setUseRealProvider(e.target.checked)}
          />
          Usar proveedor real de email (gratuito)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includePassword}
            onChange={(e) => setIncludePassword(e.target.checked)}
          />
          Incluir password único
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={withProfiles}
            onChange={(e) => setWithProfiles(e.target.checked)}
          />
          Preparar creación de perfiles digitales
        </label>
      </div>

      {useRealProvider && (
        <FormRow style={{ maxWidth: 320, marginBottom: '1rem' }}>
          <Label><FaServer /> Proveedor y modo</Label>
          <TechSelect value={providerMode} onChange={(e) => setProviderMode(e.target.value)}>
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </TechSelect>
          <Hint>Catch-all propio (cuentas.adbize.com): direcciones ilimitadas y permanentes en tu servidor, el código se lee solo. mail.tm es temporal y a veces lo bloquean IG/FB.</Hint>
        </FormRow>
      )}

      <div style={{ display: 'flex', gap: '0.55rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <TechButton type="button" onClick={handleGenerate} disabled={busy}>
          <FaRandom /> {busy ? 'Generando…' : 'Generar emails'}
        </TechButton>
        {generated.length > 0 && (
          <>
            <TechButtonGhost type="button" onClick={handleRegenerate} disabled={busy}>
              <FaRandom /> Regenerar
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={copyEmails} disabled={busy}>
              {copied === 'emails' ? <FaCheck /> : <FaCopy />} Copiar emails
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={copyCsv} disabled={busy}>
              {copied === 'csv' ? <FaCheck /> : <FaCopy />} Copiar CSV
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={downloadCsv} disabled={busy}>
              <FaFileDownload /> Descargar CSV
            </TechButtonGhost>
            {withProfiles && (
              <TechButton type="button" onClick={handleCreateProfiles} disabled={busy}>
                <FaBolt /> {busy ? 'Creando…' : `Crear ${generated.length} perfiles`}
              </TechButton>
            )}
          </>
        )}
      </div>

      {generated.length > 0 && (
        <>
          <StatRow style={{ marginBottom: '0.6rem' }}>
            <span>Total: <strong>{generated.length}</strong></span>
            <span>Dominio: <strong>{generated[0].email_address.split('@')[1]}</strong></span>
          {generated[0]._emailMeta?.browser_emulated && <span style={{ color: 'var(--alert-success)' }}><FaCloud /> Browser emulado</span>}
            <span>Plataforma: <strong>{platform}</strong></span>
          </StatRow>
          <ResultGrid>
            {generated.map((p, idx) => (
              <EmailCard key={idx} $real={!!p._emailMeta}>
                <div className="email">{p.email_address}</div>
                <div className="meta">
                  {p.display_name} · @{p.username}
                  {includePassword && ` · ${p.password}`}
                  {p._emailMeta && (
                    <span style={{ color: 'var(--alert-success)', marginLeft: 6 }}>
                      · {p._emailMeta.provider} · {p._emailMeta.domain}
                    </span>
                  )}
                </div>
              </EmailCard>
            ))}
          </ResultGrid>
        </>
      )}

      {message && (
        <Message $ok={message.ok}>{message.text}</Message>
      )}

      {emailErrors.length > 0 && (
        <Message $ok={false}>
          <strong>Errores parciales al crear emails:</strong>
          <ul style={{ margin: '4px 0 0 1.2rem', padding: 0 }}>
            {emailErrors.map((e, i) => (
              <li key={i}>#{e.index + 1}: {e.error}</li>
            ))}
          </ul>
        </Message>
      )}

      <InfoBox>
        <strong>Modo de uso recomendado:</strong>
        <NumberedList>
          <NumberedItem>
            Activá <strong>“Usar proveedor real de email”</strong> para crear cuentas reales en mail.tm desde el servidor (gratuito).
            El modo browser emula un navegador real en el screenshot-server; el modo API usa la API directa como fallback.
          </NumberedItem>
          <NumberedItem>Elegí la plataforma y configurá cantidad, género y país.</NumberedItem>
          <NumberedItem>Generá el lote. Los emails reales se asignan a perfiles aleatorios plausibles.</NumberedItem>
          <NumberedItem>Descargá el CSV para uso externo o usá “Crear perfiles” para darlos de alta automáticamente en el sistema.</NumberedItem>
          <NumberedItem>Después podés asignarles sessionid/cookies desde el detalle de cada perfil.</NumberedItem>
        </NumberedList>
      </InfoBox>
    </TechSection>
  );
};

const InfoBox = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  font-size: 0.82rem;
  color: var(--text-secondary);
  line-height: 1.55;

  strong {
    color: var(--text-primary);
    display: block;
    margin-bottom: 0.5rem;
  }
`;

export default GeneradorEmails;
